import { DateTimeSpan } from '@shared/ui-react/UtilComponents';
import { getFilenameWithVersion } from '@shared/utils/utils';
import axios from 'axios';
import { makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import { Message } from 'primereact/message';
import { Skeleton } from 'primereact/skeleton';
import * as React from 'react';
import { PdfViewer } from './PdfViewer';
import { PdfRasterViewer } from './PdfRasterViewer';
import { TxtViewer } from './TxtViewer';
import { VideoViewer } from './VideoViewer';
import { TreeNode } from '@shared/ui-native/Tree';
import { LStorage } from '@shared/Storage';
import { isVideoExtension } from '@shared/utils/FileTypeUtils';
import type { RequestConfig } from '@kubb/plugin-client/clients/fetch';
import { Auth } from '@shared/Auth';
import { ModelService } from '@shared/models/model-service';
import { pdfClient } from '@libs/clients/viz/pdfClient/pdfClient';
import { PdfStatus } from '@libs/clients/viz/models/PdfStatus';

const getApiConfiguration = async (): Promise<Partial<RequestConfig>> => {
    const token = await Auth.getToken();
    const headers = { Authorization: 'Bearer ' + token.accessToken };
    const baseUrl: string = ModelService.getVizApiBaseUrl().replace('/v1', '');
    return { baseURL: baseUrl, headers };
};

const pdfClientInstance = pdfClient();

@observer
export class PdfOrExternalViewer extends React.Component<{
    idocument: IDocument;
    width?: string;
    height?: string;
    infoPosition: string;
    hidden?: boolean;
    showFillControls?: boolean;
    showFillSuccessMessage?: boolean;
    onOpenDocumentUuid?: (documentUuid: string) => void;
    isAppEmbedded?: boolean;
}> {
    @observable isLoading = false;
    @observable isPdfStatusLoading = false;
    @observable isPdfAvailable = true;
    @observable pdfTileUrl: string = null;
    @observable pdfStatusCheckedForUuid: string = null;
    @observable pdfStatus: PdfStatus = null;

    static defaultProps = {
        width: null,
        infoPosition: 'bottom',
    };

    wrapperEl: HTMLDivElement = null;

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        this.fetchPdfStatusIfNeeded();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.idocument?.uuid !== this.props.idocument?.uuid) {
            this.fetchPdfStatusIfNeeded();
        }
    }

    isPdfDocument(): boolean {
        const ext = utils.getExtensionFromFilename(this.props.idocument.name).toLowerCase();
        return ext === 'pdf';
    }

    fetchPdfStatusIfNeeded = async () => {
        if (!this.isPdfDocument()) {
            this.isPdfAvailable = true;
            this.isPdfStatusLoading = false;
            this.pdfTileUrl = null;
            this.pdfStatusCheckedForUuid = null;
            return;
        }

        const fileUuid = this.props.idocument?.uuid;

        if (!fileUuid) {
            return;
        }

        if (this.pdfStatusCheckedForUuid === fileUuid) {
            const isProcessing = this.pdfStatus && this.pdfStatus.processing_started && !this.pdfStatus.processing_completed;
            if (!isProcessing) {
                return;
            }
        }

        this.isPdfStatusLoading = true;

        try {
            const url = `/kuura/v1/viz/pdf/${fileUuid}/status`;
            const token = await Auth.getToken();
            const resp: Response = await fetch(url, {
                headers: { Authorization: 'Bearer ' + token.accessToken },
            });

            const status = await resp.json();

            if (this.props.idocument?.uuid !== fileUuid) {
                return;
            }

            this.pdfStatus = status;
            this.isPdfAvailable = status?.available ?? true;
            this.pdfTileUrl = status?.tileUrl ?? null;
            this.pdfStatusCheckedForUuid = fileUuid;
        } catch (error) {
            console.error('Failed to fetch PDF status:', error);
            if (this.props.idocument?.uuid !== fileUuid) {
                return;
            }

            this.isPdfAvailable = true;
            this.pdfTileUrl = null;
            this.pdfStatusCheckedForUuid = fileUuid;
        } finally {
            if (this.props.idocument?.uuid === fileUuid) {
                // If the fileUuid is the same as the current fileUuid, set isPdfStatusLoading to false
                // This is to prevent the loading state from remaining true if the fileUuid changes while the request is in progress
                this.isPdfStatusLoading = false;
            }
        }
    };

    render() {
        const { idocument } = this.props;
        const ext = utils.getExtensionFromFilename(idocument.name).toLowerCase();
        let src = null;
        if (ext === 'pdf') {
            src = `/kuura/pdf/${idocument.id}.htm`;
        } else if (ext === 'txt') {
            src = `/kuura/txt/${idocument.uuid}.htm`;
        } else if (idocument.url) {
            const srcRegex = /src="([^"]*)"/;
            const match = idocument.url.match(srcRegex);
            src = match ? match[1] : idocument.url;
        }

        return (
            <>
                {this.isLoading && <Skeleton width='100%' height='100%'></Skeleton>}
                {this.isPdfStatusLoading && <Skeleton width='100%' height='100%'></Skeleton>}

                <div className='flex-column' style={{ display: this.props.hidden ? 'none' : 'flex' }}>
                    {this.props.infoPosition === 'top' ? <DocumentInfoPanel idocument={idocument} /> : null}
                    <div className='flex-column' ref={el => (this.wrapperEl = el)} style={{ minWidth: this.props.width, display: this.isLoading ? 'none' : 'flex' }}>
                        {this.renderViewer(ext, src, idocument)}
                    </div>
                    {this.props.infoPosition === 'bottom' ? <DocumentInfoPanel idocument={idocument} /> : null}
                </div>
            </>
        );
    }

    renderViewer(ext: string, src: string, idocument: IDocument) {
        if (ext === 'txt') {
            if (!src) {
                return <Message severity='error' text='TXT source not available' style={{ width: '100%' }} />;
            }
            return (
                <TxtViewer
                    txtUrl={src}
                    height={this.props.height}
                    canEdit={true}
                    documentUuid={idocument.uuid}
                    onNewVersionCreated={newFile => {
                        if (this.props.idocument) {
                            this.props.idocument.id = newFile.id;
                            this.props.idocument.uuid = newFile.uuid;
                            this.props.idocument.name = newFile.name;
                        }
                    }}
                />
            );
        }

        if (ext === 'pdf') {
            if (this.isPdfStatusLoading) {
                return null;
            }

            if (this.pdfTileUrl) {
                return <PdfRasterViewer tileUrl={this.pdfTileUrl} height={this.props.height} />;
            }

            if (!src) {
                return <Message severity='error' text='PDF source not available' style={{ width: '100%' }} />;
            }

            const templateId = this.props.idocument.reportTemplateId ?? this.props.idocument.reportTemplate?.id;
            return (
                <PdfViewer
                    pdfUrl={src}
                    height={this.props.height}
                    documentUuid={this.props.idocument.uuid}
                    templateId={templateId}
                    canFill={!!templateId}
                    onSaveSuccess={doc => (this.props.onOpenDocumentUuid ? this.props.onOpenDocumentUuid(doc.uuid) : null)}
                    showFillSuccessMessage={this.props.showFillSuccessMessage}
                    isAppEmbedded={this.props.isAppEmbedded}
                />
            );
        }

        if (isVideoExtension(ext)) {
            return <VideoViewer documentUuid={idocument.uuid} height={this.props.height || '500px'} />;
        }

        if (src && idocument.hasOwnProperty('url')) {
            return <iframe style={{ width: '100%', height: this.props.height, flex: 'auto' }} frameBorder='0' src={src} onLoad={() => (this.isLoading = false)} />;
        }

        return <Message severity='warn' text={`Unsupported file type: ${ext.toUpperCase()}`} style={{ width: '100%' }} />;
    }
}

@observer
export class DocxViewer extends React.Component<{
    document: IDocument;
    height?: string;
}> {
    @observable isLoading = true;
    @observable isError = false;
    wrapperEl: HTMLDivElement = null;
    shadowRoot: ShadowRoot = null;

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        this.init();
    }

    componentDidUpdate(prevProps: any) {
        if (prevProps.document.id !== this.props.document.id) {
            this.init();
        }
    }

    init() {
        try {
            const ro = new ResizeObserver(this.onResize);
            ro.observe(this.wrapperEl as HTMLDivElement);
        } catch {}

        const renderDoc = async () => {
            if (!this.shadowRoot) {
                this.shadowRoot = this.wrapperEl.attachShadow({ mode: 'open' }); // library will render docx to html and infrakit app styling will mess it up,so needs to be encapsulated
            }

            try {
                await (window as any).docx.renderAsync(
                    (
                        await axios.get(`${kuura.getContextPath()}/api/1/file/${this.props.document.id}/get?inline=true`, { responseType: 'arraybuffer' })
                    ).data,
                    this.shadowRoot,
                    null,
                    {
                        ignoreWidth: false,
                        inWrapper: false,
                    }
                );
                this.isLoading = false;
                this.onResize();
            } catch (e) {
                this.isLoading = false;
                this.isError = true;
            }
        };

        if (!(window as any).docx) {
            jQuery.getScript('//web.infrakit.com/lib/docxjs-viewer_0.1.17/jszip.min.js', () => {
                jQuery.getScript('//web.infrakit.com/lib/docxjs-viewer_0.1.17/docx-preview.js', () => {
                    renderDoc();
                });
            });
        } else {
            renderDoc();
        }
    }

    onResize = _.throttle(() => {
        if (!this.shadowRoot || !this.wrapperEl) return;
        const docxEls = Array.from(this.shadowRoot.querySelectorAll('.docx')) as HTMLDivElement[];
        if (!docxEls.length) return;

        const rect = this.wrapperEl.getBoundingClientRect();
        let totalHeight = 0;
        docxEls.forEach(docxEl => {
            docxEl.style.transformOrigin = 'top left';
            docxEl.style.transform = `scale(${(rect.width - 20) / docxEl.offsetWidth})`;
            totalHeight += docxEl.getBoundingClientRect().height;
        });
        this.wrapperEl.style.height = totalHeight + 20 + 'px';
    }, 30);

    render() {
        return (
            <>
                {this.isError && <Message severity='warn' text={__('general.failed_to_load_data')} style={{ width: '100%' }} />}
                {this.isLoading && <Skeleton width='100%' height='100%'></Skeleton>}

                <div ref={el => (this.wrapperEl = el)} style={{ background: 'white', overflow: 'hidden' /* parent must handle overflow (auto) */ }}></div>
            </>
        );
    }
}

@observer
export class DocumentInfoPanel extends React.Component<{
    idocument: IDocument;
    folderTreeNode?: TreeNode;
    goToFolder?: (id: number) => void;
}> {
    @observable isCollapsed = LStorage.getOrDefault('documentViewerInfoCollapsed', false);

    constructor(props: any) {
        super(props);
        makeObservable(this);
    }

    toggleCollapsed = () => {
        this.isCollapsed = !this.isCollapsed;
        LStorage.setItem('documentViewerInfoCollapsed', this.isCollapsed);
    };

    render() {
        const { idocument, folderTreeNode, goToFolder } = this.props;
        const ext = utils.getExtensionFromFilename(idocument.name).toLowerCase();
        const isVideo = isVideoExtension(ext);

        let link = null;
        let splitLink = null;
        let description = null;
        if (!!idocument.description) {
            if (idocument.description.toLowerCase().indexOf('http://') == 0 || idocument.description.toLowerCase().indexOf('https://') == 0) {
                splitLink = idocument.description.split(' ');
                link = splitLink[0];
            } else if (idocument.description.toLowerCase().indexOf('www.') == 0) {
                splitLink = idocument.description.split(' ');
                link = 'http://' + splitLink[0];
            }
            if (splitLink && splitLink[1]) {
                description = splitLink.slice();
                description[0] = ' ';
                description = description.join(' ');
            }
        }

        let parents = [];
        const folderNode = folderTreeNode;
        if (folderNode) {
            parents = folderNode.getParents();
            parents.push(folderNode);
        }

        return (
            <div id='document-popup-info'>
                <div className='image-collapsible-title' onClick={this.toggleCollapsed}>
                    <i className={!this.isCollapsed ? 'iki iki-chevron-down' : 'iki iki-chevron-up'}></i>
                    <b className={this.isCollapsed ? 'ellipsis' : null} style={{ display: 'block' }}>
                        {getFilenameWithVersion(idocument)}
                    </b>
                </div>

                {!this.isCollapsed && (
                    <>
                        {idocument.url && (
                            <div className='ellipsis clickable' onClick={() => window.open(idocument.url)} title={idocument.url}>
                                {idocument.url}
                            </div>
                        )}
                        {idocument.description && link && (
                            <div>
                                {kuura.getMessage('map.image_desc_js')}:{' '}
                                <a href={link} target='_blank'>
                                    {splitLink[0]}
                                </a>
                                {description}
                            </div>
                        )}
                        {idocument.description && !link && (
                            <div>
                                {kuura.getMessage('map.image_desc_js')}: {idocument.description}
                            </div>
                        )}
                        <div>
                            {kuura.getMessage('map.image_upload_date_js')}: <DateTimeSpan timestamp={idocument.timestamp} />
                        </div>
                        {!!idocument.creator ? (
                            <div>
                                {kuura.getMessage('map.image_creator_js')}: {idocument.creator.username}
                            </div>
                        ) : null}
                        {!isVideo && !!idocument.reportTemplate ? (
                            <div>
                                {kuura.getMessage('js_project.template_name')}: {idocument.reportTemplate.name}
                            </div>
                        ) : null}
                        {folderTreeNode ? (
                            <div>
                                <span>{kuura.getMessage('general.folder')}: </span>
                                <span>
                                    {parents.map((p, i) => (
                                        <span key={p.id} data-id={p.id} className='clickable' onClick={() => goToFolder(p.id)}>
                                            {p.ajaxData.name} {i < parents.length - 1 ? ' > ' : null}
                                        </span>
                                    ))}
                                </span>
                            </div>
                        ) : null}
                    </>
                )}
            </div>
        );
    }
}
